import { useCallback, useMemo, useState } from 'react';
import { useCustom, useCustomMutation, useList } from '@refinedev/core';
import { Search, UserPlus, Trash2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import type {
  AccessSourceType,
  AdminBookAccessRowDto,
  AdminBookPaginatedDto,
} from '@masters-club/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DataTable, type BulkAction, type Column } from '@/components/tables';
import { formatDate } from '@/components/universal';
import { useConfirm } from '@/hooks/use-confirm';
import type { User } from '@/resources/users/types';

type BookAccessTableRow = AdminBookAccessRowDto & { id: number };

const SOURCE_LABELS: Record<AccessSourceType, string> = {
  ORDER: 'Заказ',
  SUBSCRIPTION: 'Подписка',
  MANUAL: 'Ручной',
  PROMOTION: 'Промо',
  GIFT: 'Подарок',
};

const PAGE_SIZE = 20;

interface BookAccessTabProps {
  bookId: number;
}

function displayName(
  row: Pick<AdminBookAccessRowDto, 'firstName' | 'lastName' | 'username' | 'telegramId' | 'userId'>,
): string {
  const parts = [row.firstName, row.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (row.username) {
    return `@${row.username}`;
  }
  return `ID: ${row.telegramId ?? row.userId}`;
}

export function BookAccessTab({ bookId }: BookAccessTabProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isGrantOpen, setIsGrantOpen] = useState(false);

  const { confirm, ConfirmationDialog } = useConfirm();
  const { mutate: mutateAccess } = useCustomMutation();

  const { query } = useCustom<AdminBookPaginatedDto<AdminBookAccessRowDto>>({
    url: `/admin/books/${bookId}/access`,
    method: 'get',
    config: {
      query: {
        search: search || undefined,
        page,
        limit: PAGE_SIZE,
      },
    },
  });

  const refetch = query?.refetch;
  const payload = query?.data?.data;
  const items: AdminBookAccessRowDto[] = payload?.data ?? [];
  const total = payload?.meta?.total ?? items.length;
  const isLoading = query?.isLoading ?? false;

  const rows: BookAccessTableRow[] = items.map((item) => ({ ...item, id: item.grantId }));

  const handleRevoke = useCallback(async (selectedRows: BookAccessTableRow[]) => {
    if (selectedRows.length === 0) { return; }
    const isSingle = selectedRows.length === 1;
    const targetLabel = isSingle
      ? `пользователя ${displayName(selectedRows[0])}`
      : `${selectedRows.length} пользователей`;
    const ok = await confirm({
      title: 'Отозвать доступ?',
      description: `Доступ ${targetLabel} к книге будет отозван.`,
      confirmText: 'Отозвать',
      cancelText: 'Отмена',
      variant: 'destructive',
    });
    if (!ok) { return; }

    let completed = 0;
    for (const row of selectedRows) {
      mutateAccess(
        {
          url: `/admin/books/${bookId}/access/${row.userId}`,
          method: 'delete',
          values: {},
        },
        {
          onSuccess: () => {
            completed += 1;
            if (completed === selectedRows.length) {
              notify.success(isSingle ? 'Доступ отозван' : 'Доступы отозваны');
              void refetch?.();
            }
          },
          onError: (error) => {
            notify.error(error instanceof Error ? error.message : 'Не удалось отозвать доступ');
          },
        },
      );
    }
  }, [bookId, confirm, mutateAccess, refetch]);

  const columns = useMemo<Column<BookAccessTableRow>[]>(
    () => [
      {
        key: 'userId',
        header: 'Пользователь',
        render: (_, row) => (
          <div>
            <p className="font-medium text-sm">{displayName(row)}</p>
            <p className="text-xs text-muted-foreground">
              {row.email ?? (row.username ? `@${row.username}` : `ID: ${row.telegramId ?? row.userId}`)}
            </p>
          </div>
        ),
      },
      {
        key: 'accessSource',
        header: 'Источник',
        width: '140px',
        render: (_, row) => (
          <Badge variant="outline" className="text-[10px]">
            {SOURCE_LABELS[row.accessSource]}
          </Badge>
        ),
      },
      {
        key: 'accessGrantedAt',
        header: 'Выдан',
        width: '150px',
        render: (_, row) => (
          <span className="text-xs text-muted-foreground">
            {row.accessGrantedAt ? formatDate(row.accessGrantedAt) : '—'}
          </span>
        ),
      },
      {
        key: 'accessExpiresAt',
        header: 'Истекает',
        width: '150px',
        render: (_, row) => (
          <span className="text-xs text-muted-foreground">
            {row.accessExpiresAt ? formatDate(row.accessExpiresAt) : 'Бессрочно'}
          </span>
        ),
      },
    ],
    [],
  );

  const bulkActions = useMemo<BulkAction<BookAccessTableRow>[]>(
    () => [
      {
        label: 'Отозвать доступ',
        icon: <Trash2 className="h-4 w-4" />,
        variant: 'destructive',
        showOnlyWhenSelected: true,
        onClick: (selectedRows) => {
          void handleRevoke(selectedRows);
        },
      },
    ],
    [handleRevoke],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setIsGrantOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Выдать доступ
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchValue={search}
        searchPlaceholder="Поиск по имени, username, email..."
        emptyMessage={search ? 'Ничего не найдено' : 'Нет выданных доступов'}
        selectable
        bulkActions={bulkActions}
      />

      <GrantAccessDialog
        bookId={bookId}
        open={isGrantOpen}
        onOpenChange={setIsGrantOpen}
        onGranted={() => { void refetch?.(); }}
      />

      {ConfirmationDialog}
    </div>
  );
}

interface GrantAccessDialogProps {
  bookId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGranted: () => void;
}

function GrantAccessDialog({ bookId, open, onOpenChange, onGranted }: GrantAccessDialogProps) {
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const { mutate, mutation: grantMutation } = useCustomMutation();
  const isPending = grantMutation.isPending;

  const { result } = useList<User>({
    resource: 'users',
    pagination: { pageSize: 10, mode: 'server' },
    filters: userSearch
      ? [{ field: 'search', operator: 'contains', value: userSearch }]
      : [],
    queryOptions: { enabled: open },
  });

  const users: User[] = result?.data ?? [];

  const handleSubmit = () => {
    if (selectedUserId === null) {
      notify.error('Выберите пользователя');
      return;
    }

    const expiresIso = expiresAt ? expiresAt.toISOString() : null;

    mutate(
      {
        url: `/admin/books/${bookId}/access/grant`,
        method: 'post',
        values: {
          userId: selectedUserId,
          expiresAt: expiresIso,
        },
      },
      {
        onSuccess: () => {
          notify.success('Доступ выдан');
          setSelectedUserId(null);
          setUserSearch('');
          setExpiresAt(null);
          onOpenChange(false);
          onGranted();
        },
        onError: (error) => {
          notify.error(error instanceof Error ? error.message : 'Не удалось выдать доступ');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Выдать доступ</DialogTitle>
          <DialogDescription>
            Выберите пользователя и при необходимости задайте дату окончания доступа.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="book-access-user-search">Пользователь</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="book-access-user-search"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setSelectedUserId(null); }}
                placeholder="Поиск по имени, username..."
                className="pl-9"
              />
            </div>
            {users.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-md border">
                {users.map((u) => {
                  const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || `ID: ${u.telegramId}`;
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedUserId(u.id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition ${
                        selectedUserId === u.id ? 'bg-accent' : ''
                      }`}
                    >
                      <p className="font-medium">{name}</p>
                      <p className="text-xs text-muted-foreground">
                        {u.username ? `@${u.username}` : `ID: ${u.telegramId}`}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="book-access-expires-at">Срок действия (опционально)</Label>
            <DateTimePicker
              value={expiresAt}
              onChange={setExpiresAt}
              placeholder="Бессрочно"
            />
            <p className="text-xs text-muted-foreground">
              Оставьте пустым для бессрочного доступа.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={isPending || selectedUserId === null}>
            {isPending ? 'Выдача...' : 'Выдать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
